import { v4 as uuidv4 } from 'uuid';
import appManager from '../../app/appManager';
import AppModule from '../../app/lib/AppModule';
import { diagnosticsState } from './lib/state';
import {
  TrackErrorInput,
  TrackEventInput,
  TrackedError,
  TrackedErrorPriority,
  TrackedErrorType,
  TrackedEvent,
} from './lib/types';
import SentryErrorIntegration from './SentryErrorIntegration';
import extractErrorMessage from '@lib/utils/extractErrorMessage';

const ERROR_IGNORE_LIST = [
  'window.require is not implemented', // workaround for vite preview (see index.html)
  'Requested device not found', // side effect of MFE soft phone
];

function isIgnoredError(errorMessage: string) {
  return ERROR_IGNORE_LIST.some((ignore) => errorMessage.includes(ignore));
}

function trackError(errorInput: TrackErrorInput) {
  let error: Error;
  if (!errorInput.error) {
    error = new Error(errorInput.message);
  } else if (errorInput.error instanceof Error) {
    error = errorInput.error;
  } else {
    error = new Error(errorInput.message, { cause: errorInput.error });
  }

  // ignore errors from our ignore list
  if (isIgnoredError(error.message)) {
    return;
  }

  // send to Sentry
  if (errorInput.source !== 'sentry') {
    sentryErrorIntegration.captureException(error);
  }

  const item: TrackedError = {
    id: uuidv4(),
    ...errorInput,
    priority: errorInput.priority || 'normal',
    error,
    createdAt: Date.now(),
    type: 'error',
    read: false,
  };
  diagnosticsState.errors = [...diagnosticsState.errors, item];
}

export const sentryErrorIntegration = new SentryErrorIntegration(trackError);

export type ReportErrorOptions = Omit<
  TrackErrorInput,
  'errorType' | 'message' | 'error'
>;

export type ReportEventOptions = Omit<TrackEventInput, 'message'>;

class DiagnosticsModule extends AppModule {
  override preload(): void {
    appManager.events.onAny((event: string | string[], ...data: any[]) => {
      const message = Array.isArray(event) ? event.join(', ') : event;

      // report event so we can see in UI
      this.reportEvent(message, {
        source: 'app',
        extra: data,
      });
      this.app.logger.info('event', event, data);

      if (event === 'error' && !isIgnoredError(data[0].message)) {
        // these are handled, log.error(...) calls
        // report so it goes through the normal error flow
        this.reportBackgroundError(data[0].args, data[0].message, {
          source: 'app',
          priority: 'normal',
        });
      }
    });
  }

  public reportUserError(message: string, options?: ReportErrorOptions): void;
  public reportUserError(
    error: Error | unknown,
    options?: ReportErrorOptions,
  ): void;
  public reportUserError(
    error: Error | unknown,
    message: string,
    options?: ReportErrorOptions,
  ): void;
  public reportUserError(
    error: any,
    message: any,
    options?: ReportErrorOptions,
  ): void {
    this.reportError(error, message, options, 'user');
  }

  public reportBackgroundError(
    message: string,
    options?: ReportErrorOptions,
  ): void;
  public reportBackgroundError(
    error: Error | unknown,
    options?: ReportErrorOptions,
  ): void;
  public reportBackgroundError(
    error: Error | unknown,
    message: string,
    options?: ReportErrorOptions,
  ): void;
  public reportBackgroundError(
    error: any,
    message: any,
    options?: ReportErrorOptions,
  ): void {
    this.reportError(error, message, options, 'background');
  }

  public reportEvent(message: string, options: ReportEventOptions) {
    const item: TrackedEvent = {
      ...options,
      id: uuidv4(),
      message,
      createdAt: Date.now(),
      type: 'event',
    };
    diagnosticsState.events = [...diagnosticsState.events, item];
  }

  public clearItems() {
    diagnosticsState.errors = [];
    diagnosticsState.events = [];
  }

  public markErrorsAsRead(priorities?: TrackedErrorPriority[]) {
    diagnosticsState.errors.forEach((error) => {
      if (!priorities || priorities.includes(error.priority)) {
        error.read = true;
      }
    });
  }

  public setDiagnosticsDrawerOpen(open: boolean) {
    diagnosticsState.drawerOpen = open;
  }

  private reportError(
    arg1: any,
    arg2: any,
    arg3?: ReportErrorOptions,
    errorType: TrackedErrorType = 'user',
  ): void {
    let actualMessage: string | undefined = arg2;
    let actualError: Error | unknown | undefined = arg1;

    if (typeof arg2 === 'object') {
      // arg2 is options, arg1 is a message or error
      arg3 = arg2;
      actualMessage = typeof arg1 === 'string' ? arg1 : undefined;
      actualError = typeof arg1 !== 'string' ? arg1 : undefined;
    }

    if (!actualMessage) {
      actualMessage = extractErrorMessage(actualError);
    }

    trackError({
      message: actualMessage,
      error: actualError,
      ...arg3,
      errorType,
    });
  }
}

export default new DiagnosticsModule(
  appManager.events,
  appManager.logger('Diagnostics'),
);
